import { useState } from 'react'
import { Modal } from './Modal'
import { formatDayDate } from '../lib/dates'
import { DAY_NAMES, DAY_STATUSES, type DayStatus, type Lunch } from '@shared/types'

interface Props {
  lunch: Lunch
  weekStart: string
  onSave: (patch: { status: DayStatus; label: string }) => void
  onSwap: (otherDay: number) => void
  onClose: () => void
}

/**
 * Per-day controls: how the day is handled, an optional note, and a direct
 * swap with any other weekday -- the fast path when the five lunches are
 * right but land on the wrong days.
 */
export function DaySettings({ lunch, weekStart, onSave, onSwap, onClose }: Props) {
  const [status, setStatus] = useState<DayStatus>(lunch.status)
  const [label, setLabel] = useState(lunch.label)
  const dayName = DAY_NAMES[lunch.dayOfWeek - 1]!

  return (
    <Modal
      title={`${dayName}, ${formatDayDate(weekStart, lunch.dayOfWeek)}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <div className="spacer" />
          <button className="btn primary" onClick={() => onSave({ status, label })}>
            Save
          </button>
        </>
      }
    >
      <div className="field">
        <label>This day</label>
        <div className="picker-list">
          {DAY_STATUSES.map((option) => (
            <button
              key={option.value}
              className="picker-option"
              aria-pressed={status === option.value}
              style={
                status === option.value
                  ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
                  : undefined
              }
              onClick={() => setStatus(option.value)}
            >
              <span className="thumb" aria-hidden="true">
                {option.emoji}
              </span>
              <span className="name">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="day-label">Note (optional)</label>
        <input
          id="day-label"
          type="text"
          autoComplete="off"
          value={label}
          placeholder="Vacation, field trip, half day…"
          maxLength={80}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>

      <div className="field">
        <label>Swap this whole lunch with</label>
        <div className="row wrap">
          {DAY_NAMES.map((name, index) => {
            const target = index + 1
            if (target === lunch.dayOfWeek) return null
            return (
              <button key={name} className="btn sm" onClick={() => onSwap(target)}>
                {name}
              </button>
            )
          })}
        </div>
        <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
          Moves the items, the status, and the note together.
        </p>
      </div>
    </Modal>
  )
}
